// Variant gating for variety pages. Driven by real variant availability,
// not tag priority: a variety can be open on more than one channel at once,
// and forcing a single winner hides real stock.
// ?channel=delivery|farm narrows the page to one journey. It never touches
// the nav; the menu is identical on every page.

const availabilityCache = new Map();

const CHANNEL_PARAM = 'channel';

function currentChannel() {
  const value = new URLSearchParams(window.location.search).get(CHANNEL_PARAM);
  if (value === 'pickup') return 'farm'; // legacy alias — pre-rename shared links
  return value === 'delivery' || value === 'farm' ? value : null;
}

function propagateChannelContext() {
  document.querySelectorAll('[data-channel-context]').forEach((grid) => {
    const context = grid.dataset.channelContext;
    if (context !== 'delivery' && context !== 'farm' && context !== 'pickup') return;
    grid.querySelectorAll('a[href*="/products/"]').forEach((link) => {
      try {
        const url = new URL(link.href, window.location.origin);
        if (url.searchParams.get(CHANNEL_PARAM) === context) return;
        url.searchParams.set(CHANNEL_PARAM, context);
        link.href = `${url.pathname}${url.search}${url.hash}`;
      } catch (e) {
      }
    });
  });
}

function optionKey(purchaseType, format) {
  return `${purchaseType}||${format}`;
}

async function loadAvailability(handle) {
  if (availabilityCache.has(handle)) return availabilityCache.get(handle);
  try {
    const res = await fetch(`/products/${handle}.js`);
    if (!res.ok) throw new Error(String(res.status));
    const product = await res.json();
    const pairs = new Set();
    const byType = new Set();
    product.variants.forEach((variant) => {
      if (!variant.available) return;
      const [purchaseType, format] = variant.options;
      pairs.add(optionKey(purchaseType, format));
      byType.add(purchaseType);
    });
    const data = { pairs, byType };
    availabilityCache.set(handle, data);
    return data;
  } catch (e) {
    availabilityCache.set(handle, null);
    return null;
  }
}

function hideOptionRowIfSingle(keptInputs, allInputs) {
  if (keptInputs.length !== 1 || allInputs.length < 2) return false;
  const row =
    keptInputs[0].closest('.variant-option') || keptInputs[0].closest('[class*="variant-option"]');
  if (!row || row.style.display === 'none') return false;
  row.style.display = 'none';
  return true;
}

function applyGate(inputs, isAllowed) {
  const toHide = [];
  const toKeep = [];

  inputs.forEach((input) => {
    (isAllowed(input.value) ? toKeep : toHide).push(input);
  });

  if (!toKeep.length) return false;

  let changed = false;
  let needsReselect = false;

  toHide.forEach((input) => {
    const label = input.closest('.variant-option__button-label');
    if (label && label.style.display !== 'none') {
      label.style.display = 'none';
      changed = true;
    }
    if (!input.disabled) {
      input.disabled = true;
      changed = true;
    }
    if (input.checked) needsReselect = true;
  });

  if (hideOptionRowIfSingle(toKeep, inputs)) changed = true;

  if (needsReselect) {
    const next = toKeep[0];
    next.checked = true;
    next.dispatchEvent(new Event('change', { bubbles: true }));
    changed = true;
  }

  return changed;
}

function isSingleStemValue(value) {
  return /single\s*stem/i.test(value);
}

function isPreorderValue(value) {
  return /pre[\s-]*order/i.test(value);
}

const OPEN_CHANNEL_PANELS = [
  '.fulfilment-gate__panel--shipping',
  '.fulfilment-gate__panel--preorder',
  '.fulfilment-gate__panel--pickup',
];

function pairChannelPanel(purchaseType, format) {
  if (isSingleStemValue(format)) return '.fulfilment-gate__panel--pickup';
  return isPreorderValue(purchaseType)
    ? '.fulfilment-gate__panel--preorder'
    : '.fulfilment-gate__panel--shipping';
}

function pairInChannel(purchaseType, format, channel) {
  if (!channel) return true;
  const single = isSingleStemValue(format);
  return channel === 'farm' ? single : !single;
}

function renderChannelUnavailable(gate, channel, availability) {
  const scope = gate.closest('.product-details') || document;
  [scope.querySelector('variant-picker'), scope.querySelector('product-form-component')].forEach((el) => {
    if (!el) return;
    el.style.display = 'none';
    setControlsDisabled(el, true);
  });
  setControlsDisabled(gate, true);

  if (gate.querySelector('[data-channel-unavailable]')) return;

  const elsewhere = [...availability.pairs].some((key) =>
    pairInChannel(...key.split('||'), channel === 'delivery' ? 'farm' : 'delivery')
  );

  const panel = document.createElement('div');
  panel.className = 'fulfilment-gate__panel fulfilment-gate__panel--unavailable';
  panel.setAttribute('data-channel-unavailable', '');

  const note = document.createElement('p');
  note.className = 'fulfilment-gate__note';
  if (elsewhere && channel === 'delivery') {
    note.innerHTML =
      'We only have this variety available to collect at the farm right now. ' +
      '<a href="/pages/pickup-farm-gate">Visit the Farm</a>.';
  } else if (elsewhere) {
    note.innerHTML =
      'We only have this variety available for delivery right now. ' +
      '<a href="/pages/shop-by-dispatch-date">See delivery options</a>.';
  } else {
    note.textContent = "This variety isn't open for sale yet. Check back soon.";
  }
  panel.appendChild(note);
  gate.appendChild(panel);
}

function setControlsDisabled(root, disabled) {
  let changed = false;
  root.querySelectorAll('input, select, textarea, button').forEach((control) => {
    if (disabled) {
      if (control.disabled) return; // not ours — leave the theme's own disable alone
      control.disabled = true;
      control.setAttribute('data-gate-disabled', '');
      changed = true;
    } else if (control.hasAttribute('data-gate-disabled')) {
      control.disabled = false;
      control.removeAttribute('data-gate-disabled');
      changed = true;
    }
  });
  return changed;
}

function gateFormatOptions(gate, availability) {
  if (!availability) return false;

  const channel = currentChannel();
  const scope = gate.closest('.product-details') || document;
  const purchaseInputs = [...scope.querySelectorAll('input[data-option-name="Purchase Type" i]')];
  const formatInputs = [...scope.querySelectorAll('input[data-option-name="Format" i]')];
  if (!purchaseInputs.length && !formatInputs.length) return false;

  const pairs = [...availability.pairs].filter((key) => {
    const [purchaseType, format] = key.split('||');
    return pairInChannel(purchaseType, format, channel);
  });
  let changed = false;

  const hideSelectors = !channel
    ? []
    : channel === 'farm'
    ? ['.fulfilment-gate__panel--shipping', '.fulfilment-gate__panel--preorder']
    : ['.fulfilment-gate__panel--pickup'];

  if (formatInputs.length) {
    const stocked = new Set(pairs.map((key) => pairChannelPanel(...key.split('||'))));
    if (stocked.size) {
      OPEN_CHANNEL_PANELS.forEach((selector) => {
        if (!stocked.has(selector)) hideSelectors.push(selector);
      });
    }
  }

  gate.querySelectorAll('.fulfilment-gate__panel').forEach((panel) => {
    if (hideSelectors.some((selector) => panel.matches(selector)) && panel.style.display !== 'none') {
      panel.style.display = 'none';
      changed = true;
    }
    changed = setControlsDisabled(panel, panel.style.display === 'none') || changed;
  });

  const lead = gate.querySelector('[data-gate-lead]');
  if (lead) {
    const openCount = [...gate.querySelectorAll(OPEN_CHANNEL_PANELS.join(','))].filter(
      (panel) => panel.style.display !== 'none'
    ).length;
    const hideLead = openCount < 2;
    if ((lead.style.display === 'none') !== hideLead) {
      lead.style.display = hideLead ? 'none' : '';
      changed = true;
    }
  }

  if (!pairs.length) {
    renderChannelUnavailable(gate, channel, availability);
    return true;
  }
  const usable = new Set(pairs);
  const typesInChannel = new Set([...usable].map((key) => key.split('||')[0]));

  if (purchaseInputs.length) {
    changed = applyGate(purchaseInputs, (value) => typesInChannel.has(value)) || changed;
  }

  const selectedType = purchaseInputs.find((i) => i.checked)?.value;
  if (formatInputs.length && selectedType) {
    changed =
      applyGate(formatInputs, (value) => usable.has(optionKey(selectedType, value))) || changed;
  }

  const farmPanel = gate.querySelector('.fulfilment-gate__panel--pickup');
  const selectedFormat = formatInputs.find((i) => i.checked)?.value;
  if (farmPanel && farmPanel.style.display !== 'none' && selectedFormat) {
    changed = setControlsDisabled(farmPanel, !isSingleStemValue(selectedFormat)) || changed;
  }

  if (selectedFormat) {
    ['.fulfilment-gate__panel--shipping', '.fulfilment-gate__panel--preorder'].forEach((selector) => {
      const panel = gate.querySelector(selector);
      if (!panel || panel.style.display === 'none') return;
      changed = setControlsDisabled(panel, isSingleStemValue(selectedFormat)) || changed;
    });
  }

  return changed;
}

function hidePurchaseUI(gate) {
  if (gate.dataset.hidePurchase !== 'true') return false;

  const scope = gate.closest('.product-details') || document;
  const picker = scope.querySelector('variant-picker');
  const form = scope.querySelector('product-form-component');
  const price = scope.querySelector('product-price');
  const sticky = document.querySelector('sticky-add-to-cart');

  let changed = false;
  [picker, price, form, sticky].forEach((el) => {
    if (el && el.style.display !== 'none') {
      el.style.display = 'none';
      changed = true;
    }
  });
  [picker, form, sticky, gate].forEach((el) => {
    if (el) changed = setControlsDisabled(el, true) || changed;
  });
  return changed;
}

async function watch(gate) {
  const picker = (gate.closest('.product-details') || document).querySelector('variant-picker');

  hidePurchaseUI(gate);
  if (!picker) return;

  const handle = picker.dataset.productHandle;
  if (!handle) return;

  const availability = await loadAvailability(handle);
  gateFormatOptions(gate, availability);

  let applying = false;

  picker.addEventListener('change', () => {
    if (applying) return;
    applying = true;
    gateFormatOptions(gate, availabilityCache.get(handle));
    applying = false;
  });

  const observer = new MutationObserver(() => {
    if (applying) return;
    applying = true;
    gateFormatOptions(gate, availabilityCache.get(handle));
    hidePurchaseUI(gate);
    applying = false;
  });

  observer.observe(picker, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['style', 'disabled', 'checked'],
  });
}

function init() {
  propagateChannelContext();
  document.querySelectorAll('.fulfilment-gate[data-has-pickup]').forEach(watch);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
